/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { CSSProperties } from "react";

import { BlockType } from "@vienna/common/src/entities/block-snapshot.entity";

import { Block } from "src/classes/block";
import { BlockWindow, FileBlock, LinkBlock } from "src/classes/block/block-types";

export class StyleConstants {
  public static staticBlockWidth = 262;

  public static minBlockWidth = 100;

  public static minBlockHeight = 100;

  public static defaultBlockWidth = 300;

  public static defaultBlockHeight = 300;

  public static defaultDrilledBlockStyles: CSSProperties = {
    maxWidth: "600px",
    minWidth: "30%",
    maxHeight: "90%",
    minHeight: "40%",
  };

  public static getStaticBlockHeight(block: Block): number {
    switch (block.snapshot.type) {
      case BlockType.Text:
      case BlockType.Code:
      case BlockType.Color:
        return StyleConstants.staticBlockWidth;
      case BlockType.Image:
        const imageAR = block.snapshot.width / block.snapshot.height;
        return StyleConstants.staticBlockWidth / imageAR;
      case BlockType.File:
        const fileBlock = block as FileBlock;
        if (fileBlock.isVideo) {
          const videoAR = block.snapshot.width / block.snapshot.height;
          return StyleConstants.staticBlockWidth / videoAR + 35;
        }
        return StyleConstants.staticBlockWidth;
      case BlockType.Link:
        const linkBlock = block as LinkBlock;
        if (linkBlock.isSpotify) return 100;
        if (linkBlock.isTweet) return StyleConstants.staticBlockWidth + 50;
        if (linkBlock.isYoutube) return 180;
        return StyleConstants.staticBlockWidth;
      case BlockType.PlateWindow:
        return StyleConstants.staticBlockWidth + 180;
      case BlockType.BlockWindow:
        const blockWindow = block as BlockWindow;
        return blockWindow.embeddedBlock
          ? StyleConstants.getStaticBlockHeight(blockWindow.embeddedBlock)
          : StyleConstants.staticBlockWidth;
      default:
        return StyleConstants.staticBlockWidth;
    }
  }

  public static getDrilledViewStyles(block: Block): CSSProperties {
    switch (block.snapshot.type) {
      case BlockType.Text:
      case BlockType.Code:
      case BlockType.Color:
      case BlockType.PlateWindow:
        return StyleConstants.defaultDrilledBlockStyles;
      case BlockType.Image:
        return { maxHeight: "100%", padding: "20px 20px 20px 0" };
      case BlockType.File:
        const fileBlock = block as FileBlock;
        if (fileBlock.isPDF) {
          return {
            width: "100%",
            maxWidth: "850px",
            height: "98%",
          };
        }
        if (fileBlock.isVideo) {
          return { width: "80%", maxHeight: "100%" };
        }
        return { maxWidth: "550px", maxHeight: "550px" };
      case BlockType.Link:
        const linkBlock = block as LinkBlock;
        if (linkBlock.isSpotify) return { minWidth: "50%" };
        return StyleConstants.defaultDrilledBlockStyles;
      case BlockType.BlockWindow:
        const blockWindow = block as BlockWindow;
        return blockWindow.embeddedBlock
          ? StyleConstants.getDrilledViewStyles(blockWindow.embeddedBlock)
          : StyleConstants.defaultDrilledBlockStyles;
      default:
        return StyleConstants.defaultDrilledBlockStyles;
    }
  }

  public static estimateTextBlockHeight(text: string, width: number): number {
    const charsPerRowGuess = 0.125 * width;
    const characters = text.length;
    const rows = Math.ceil(characters / charsPerRowGuess);
    const rowHeightGuess = 25;
    return rows * rowHeightGuess;
  }
}
